import { useCallback, useEffect, useMemo, useState } from "react";

import type { WalletState } from "../lib/wallet.js";
import type { QuoteSubmission } from "../types.js";

import {
  EXECUTOR_ADDRESSES,
  findPreset,
  presetsFor,
  type ContractPreset,
  type PresetPosition,
} from "../data/contractPresets.js";
import { findToken, type ChainDef, type TokenDef } from "../data/tokens.js";
import { toBaseUnits } from "../lib/format.js";
import styles from "../styles/QuoteForm.module.css";
import { BalancePill } from "./BalancePill.js";
import { ChainSelect } from "./ChainSelect.js";
import { TokenSelect } from "./TokenSelect.js";

/**
 * The big form. Renders the inputs needed to assemble a `BuildTxInput`
 * the worker accepts. Local state only — submission bubbles up the
 * already-built input shape so App.tsx doesn't repeat the assembly.
 *
 * BEFORE/AFTER toggle dynamically shows/hides the `before` block (output
 * token + outputMinAmount) vs the AFTER block (contractCalls). V1.0
 * supports a single contract call entry to keep the surface minimal;
 * V1.1 adds dynamic add/remove of calls.
 */
export interface QuoteFormProps {
  readonly loading: boolean;
  /**
   * Connected wallet, if any. Used to:
   *   - Auto-fill `fromAddress` (so LI.FI's `msg.sender ≡ fromAddress`
   *     invariant holds at signing time — before this prop the form
   *     defaulted to the Executor placeholder, which made the LI.FI
   *     router reject the swap even though the user had approved with
   *     their real wallet).
   *   - Render balances under each token picker via `BalancePill`.
   */
  readonly wallet: WalletState | undefined;
  /**
   * Chains + tokens catalog from the GraphQL API, filtered by the
   * caller's IntegratorPolicy. Empty array means catalog not yet
   * loaded (key empty, network error, or initial render).
   */
  readonly chains: readonly ChainDef[];
  /**
   * Bump counter from App.tsx: increments every time `StepsExecutor`
   * reports all steps as confirmed on-chain. Forwarded to each
   * BalancePill so it refetches `balanceOf` against the now-updated
   * state. Defaults to 0 in callers that don't need this signal.
   */
  readonly balanceRefreshKey?: number;
  readonly onSubmit: (submission: QuoteSubmission) => void | Promise<void>;
}

const DEFAULT_SLIPPAGE = "0.005";

/**
 * LI.FI rejects `0x000…000` as either fromAddress or contractAddress
 * (validation error: "Zero address is provided"). For quote-only flows
 * we use a documented real address as a stand-in: the deployed
 * Executor on Polygon (0x151F…B9BC). When the user later signs a tx,
 * the StepsExecutor overrides `account` with the connected wallet, so
 * this address never makes it on-chain — it just satisfies the LI.FI
 * input validator at quote time.
 */
const QUOTE_PLACEHOLDER_ADDRESS = "0x151F3138700E2142b00A1482224D82ADa60AB9BC";

/**
 * Sentinel id meaning "no real business call attached — use the
 * placeholder contractAddress/callData=0x". The worker still produces a
 * quote because LI.FI only cares about the swap leg.
 */
const PRESET_NONE = "none";

/** Sentinel id meaning "user pasted custom contractAddress + callData." */
const PRESET_CUSTOM = "custom";

export function QuoteForm({
  loading,
  wallet,
  chains,
  balanceRefreshKey,
  onSubmit,
}: QuoteFormProps): React.JSX.Element {
  const initialChain: ChainDef | undefined = chains[0];
  const [fromChain, setFromChain] = useState<ChainDef | undefined>(
    initialChain,
  );
  const [toChain, setToChain] = useState<ChainDef | undefined>(initialChain);
  const [fromToken, setFromToken] = useState<TokenDef | undefined>(
    initialChain?.tokens[1],
  );
  const [toToken, setToToken] = useState<TokenDef | undefined>(
    initialChain?.tokens[2],
  );
  const [humanAmount, setHumanAmount] = useState("150");
  const [mode, setMode] = useState<"EXACT_IN" | "EXACT_OUT">("EXACT_OUT");
  const [position, setPosition] = useState<PresetPosition>("AFTER");
  const [slippage, setSlippage] = useState(DEFAULT_SLIPPAGE);

  /**
   * Selected contract preset id — drives the contractCalls[0] shape:
   *   - "none"   → placeholder address + 0x calldata (quote-only)
   *   - "custom" → user pastes contractAddress / callData / gasLimit / approvalAddress
   *   - <id>     → loaded from `contractPresets.ts`; the form auto-locks
   *                bridgeToken to the preset's `inputToken` and rebuilds
   *                calldata each time the user edits address/amount/etc.
   */
  const [presetId, setPresetId] = useState<string>(PRESET_NONE);
  const [customContractAddress, setCustomContractAddress] = useState("");
  const [customCallData, setCustomCallData] = useState("0x");
  const [customGasLimit, setCustomGasLimit] = useState("100000");
  const [customApprovalAddress, setCustomApprovalAddress] = useState("");

  /**
   * Sync chain/token state when the catalog arrives (empty → populated)
   * or is replaced (e.g. user pasted a new API key whose IntegratorPolicy
   * exposes a different set of chains). Only fills slots that are still
   * undefined — once the user makes a manual selection we don't clobber
   * it on subsequent re-renders of the same `chains` array.
   */
  useEffect(() => {
    /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */
    const first = chains[0];
    if (first === undefined) {
      // Catalog vacío (api key vacía / fetch en vuelo / error). Limpiar
      // selecciones para que el siguiente catálogo se hidrate desde 0.
      setFromChain(undefined);
      setToChain(undefined);
      setFromToken(undefined);
      setToToken(undefined);
      return;
    }
    // Re-resolver cada selección contra el NUEVO catálogo:
    //   - Si el chainId actual sigue en el catálogo → tomar el nuevo
    //     objeto (los tokens internos pueden haber cambiado por la
    //     policy del nuevo tenant).
    //   - Si no → caer al primero del catálogo.
    // Los tokens se re-resuelven en los useEffect dependientes de
    // fromChain/toChain de abajo (cadena: chains → chain → token).
    setFromChain((current) => {
      if (current === undefined) return first;
      return chains.find((c) => c.id === current.id) ?? first;
    });
    setToChain((current) => {
      if (current === undefined) return first;
      return chains.find((c) => c.id === current.id) ?? first;
    });
    /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */
  }, [chains]);

  // Re-resolve fromToken whenever fromChain changes (which itself reacts to
  // chains updates above). Preserves the user's symbol/address choice if the
  // same token exists in the new chain; otherwise falls back to index 1
  // (typical first non-native stable) and finally to index 0 (native).
  useEffect(() => {
    /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */
    if (fromChain === undefined) {
      setFromToken(undefined);
      return;
    }
    setFromToken((current) => {
      const fallback = fromChain.tokens[1] ?? fromChain.tokens[0];
      if (current === undefined) return fallback;
      const match = fromChain.tokens.find(
        (t) => t.address.toLowerCase() === current.address.toLowerCase(),
      );
      return match ?? fallback;
    });
    /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */
  }, [fromChain]);

  useEffect(() => {
    /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */
    if (toChain === undefined) {
      setToToken(undefined);
      return;
    }
    setToToken((current) => {
      const fallback =
        toChain.tokens[2] ?? toChain.tokens[1] ?? toChain.tokens[0];
      if (current === undefined) return fallback;
      const match = toChain.tokens.find(
        (t) => t.address.toLowerCase() === current.address.toLowerCase(),
      );
      return match ?? fallback;
    });
    /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */
  }, [toChain]);

  /** Whichever token the `amount` field is measured against right now. */
  const amountToken = useMemo<TokenDef | undefined>(
    () => (mode === "EXACT_IN" ? fromToken : toToken),
    [mode, fromToken, toToken],
  );

  /** Currently-active preset (if any). */
  const activePreset = useMemo<ContractPreset | undefined>(
    () =>
      presetId !== PRESET_NONE && presetId !== PRESET_CUSTOM
        ? findPreset(presetId)
        : undefined,
    [presetId],
  );

  /**
   * Presets available for the active (destination chain, position) pair.
   * The dropdown reshuffles automatically whenever the user flips the
   * position toggle.
   */
  const presetsAvailable = useMemo<readonly ContractPreset[]>(
    () => (toChain !== undefined ? presetsFor(toChain.id, position) : []),
    [toChain, position],
  );

  /**
   * Selecting a preset locks **one** of the form's tokens so the swap
   * leg matches the contract's expectations:
   *
   *   - AFTER  → preset's `bridgeAsset` lands as `toToken` (the swap
   *              output that the contract will consume).
   *   - BEFORE → preset's `userHoldsAsset` lands as `fromToken` (the
   *              a-token / asset the Executor needs to pull). The user
   *              keeps freedom on `toToken` (= the final output).
   *
   * When the user flips position, presetId is reset so a stale BEFORE
   * preset doesn't linger while we're in AFTER mode.
   */
  const onPickPreset = useCallback(
    (id: string) => {
      setPresetId(id);
      if (id === PRESET_NONE || id === PRESET_CUSTOM) return;
      const preset = findPreset(id);
      if (preset === undefined) return;
      const targetChain = chains.find((c) => c.id === preset.chainId);
      if (preset.position === "AFTER") {
        if (targetChain !== undefined) setToChain(targetChain);
        const bridge = findToken(preset.chainId, preset.bridgeAsset);
        if (bridge !== undefined) setToToken(bridge);
      } else {
        // BEFORE: lock fromToken to what the user must hold.
        if (targetChain !== undefined) setFromChain(targetChain);
        const userHolds =
          preset.userHoldsAsset !== undefined
            ? findToken(preset.chainId, preset.userHoldsAsset)
            : undefined;
        if (userHolds !== undefined) setFromToken(userHolds);
      }
    },
    [chains],
  );

  /**
   * Flipping position clears the preset selection — AFTER presets and
   * BEFORE presets never coexist, so retaining the old id is at best
   * stale (dropdown re-renders empty) and at worst plain wrong.
   *
   * BEFORE forces EXACT_IN: the core rejects BEFORE+EXACT_OUT because
   * the required pre-swap input amount (e.g. aPolUSDT to withdraw)
   * depends on the swap's reverse pricing, which LI.FI doesn't expose
   * deterministically in a single quote. EXACT_IN is well-defined:
   * "burn this much a-token, see what comes out the other end".
   */
  const onPickPosition = useCallback((next: PresetPosition) => {
    setPosition(next);
    setPresetId(PRESET_NONE);
    if (next === "BEFORE") setMode("EXACT_IN");
  }, []);

  const onPickFromChain = useCallback(
    (id: number) => {
      const next = chains.find((c) => c.id === id);
      if (next === undefined) return;
      setFromChain(next);
      setFromToken(next.tokens[1] ?? next.tokens[0]);
    },
    [chains],
  );
  const onPickToChain = useCallback(
    (id: number) => {
      const next = chains.find((c) => c.id === id);
      if (next === undefined) return;
      setToChain(next);
      setToToken(next.tokens[2] ?? next.tokens[1] ?? next.tokens[0]);
    },
    [chains],
  );

  const onPickFromToken = useCallback(
    (addr: string) => {
      const next = fromChain?.tokens.find((t) => t.address === addr);
      if (next !== undefined) setFromToken(next);
    },
    [fromChain],
  );
  const onPickToToken = useCallback(
    (addr: string) => {
      const next = toChain?.tokens.find((t) => t.address === addr);
      if (next !== undefined) setToToken(next);
    },
    [toChain],
  );

  const onSwapDirection = useCallback(() => {
    setFromChain(toChain);
    setToChain(fromChain);
    setFromToken(toToken);
    setToToken(fromToken);
  }, [fromChain, toChain, fromToken, toToken]);

  const sameTokenError = useMemo<string | undefined>(() => {
    if (
      fromChain !== undefined &&
      toChain !== undefined &&
      fromToken !== undefined &&
      toToken !== undefined &&
      fromChain.id === toChain.id &&
      fromToken.address.toLowerCase() === toToken.address.toLowerCase()
    ) {
      return "Selecciona tokens distintos en la misma chain";
    }
    return undefined;
  }, [fromChain, toChain, fromToken, toToken]);

  const validationError = useMemo<string | undefined>(() => {
    if (amountToken === undefined) return "Catalog not loaded";
    if (sameTokenError !== undefined) return sameTokenError;
    if (humanAmount.trim() === "" || humanAmount === "0")
      return "Amount must be > 0";
    const base = toBaseUnits(humanAmount, amountToken.decimals);
    if (base === undefined) return "Amount is not a valid number";
    if (slippage.trim() === "") return "Slippage required";
    const n = Number(slippage);
    if (!Number.isFinite(n) || n < 0 || n > 0.05)
      return "Slippage must be between 0 and 0.05 (5%)";
    return undefined;
  }, [humanAmount, amountToken, slippage, sameTokenError]);

  const onSubmitForm = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (validationError !== undefined) return;
      // Defensive: validationError already returns when amountToken /
      // chains are missing, but TS doesn't know that across the closure.
      if (
        fromChain === undefined ||
        toChain === undefined ||
        fromToken === undefined ||
        toToken === undefined ||
        amountToken === undefined
      )
        return;

      const baseUnits = toBaseUnits(humanAmount, amountToken.decimals);
      if (baseUnits === undefined) return; // already guarded by validationError, defensive

      // fromAddress: connected wallet wins; falls back to the quote-only
      // placeholder when no wallet is attached. The Executor placeholder
      // produces an unsignable quote but is fine for price discovery.
      const effectiveFromAddress =
        wallet !== undefined
          ? (wallet.address as `0x${string}`)
          : (QUOTE_PLACEHOLDER_ADDRESS as `0x${string}`);
      // Presets dictate which token slot is locked:
      //   - AFTER preset → bridgeToken = preset.bridgeAsset (the swap
      //     output the contract will consume).
      //   - BEFORE preset → bridgeToken = preset.bridgeAsset (what the
      //     contract PRODUCES), and `toToken` becomes the user-chosen
      //     outputToken (passed in the `before` block below).
      //   - No preset → bridgeToken = whatever the user picked as toToken.
      const effectiveBridgeToken =
        activePreset !== undefined ? activePreset.bridgeAsset : toToken.address;

      // Build the contractCalls[0] entry per the selected source:
      //   - "none"   → quote-only placeholder (works against any chain).
      //   - "custom" → user-pasted contractAddress + callData (advanced).
      //   - preset   → viem-encoded calldata, locked to the preset's input
      //                token + auto `amountPlaceholder` in EXACT_IN.
      // Executor address for BEFORE presets — needed as the `to`
      // argument of withdraw-like calls so the bridge asset lands in
      // the Executor (not the user) for the subsequent swap leg.
      const executorAddress = (EXECUTOR_ADDRESSES[fromChain.id] ??
        QUOTE_PLACEHOLDER_ADDRESS) as `0x${string}`;

      // `null` means quote-only (preset="none"): the request omits the
      // `contractCalls` field entirely so the worker routes via LI.FI's
      // `getQuote` (works cross-chain). Placeholder calldata="0x" used
      // to be sent here but LI.FI 404s on it cross-chain.
      let contractCall: Record<string, unknown> | null;
      if (activePreset !== undefined) {
        // In EXACT_OUT we know the consumed amount at quote time — bake
        // it into the calldata. In EXACT_IN we don't yet (the worker
        // patches it via amountPlaceholder after LI.FI quotes the swap).
        const presetAmount = mode === "EXACT_OUT" ? BigInt(baseUnits) : 0n;
        contractCall = {
          contractAddress: activePreset.contractAddress,
          callData: activePreset.buildCallData({
            fromAddress: effectiveFromAddress,
            amount: presetAmount,
            executorAddress,
          }),
          gasLimit: activePreset.gasLimit,
          ...(activePreset.approvalAddress !== undefined
            ? { approvalAddress: activePreset.approvalAddress }
            : {}),
          ...(mode === "EXACT_IN" &&
          activePreset.amountPlaceholder !== undefined
            ? { amountPlaceholder: activePreset.amountPlaceholder }
            : {}),
        };
      } else if (presetId === PRESET_CUSTOM) {
        contractCall = {
          contractAddress:
            customContractAddress.trim() !== ""
              ? customContractAddress.trim()
              : QUOTE_PLACEHOLDER_ADDRESS,
          callData: customCallData.trim() !== "" ? customCallData.trim() : "0x",
          gasLimit:
            customGasLimit.trim() !== "" ? customGasLimit.trim() : "100000",
          ...(customApprovalAddress.trim() !== ""
            ? { approvalAddress: customApprovalAddress.trim() }
            : {}),
        };
      } else {
        // "none" — quote-only. Omit contractCalls from the payload so the
        // worker routes via LI.FI getQuote (works for cross-chain too).
        contractCall = null;
      }
      // Per-call amount semantics (only applies when a contractCall exists):
      //   - EXACT_OUT: the exact bridgeToken amount the call will consume.
      //   - EXACT_IN + AFTER: a non-zero lower bound. LI.FI uses it for
      //     routing; the worker patches the real value into the calldata
      //     when an `amountPlaceholder` is set.
      //   - EXACT_IN + BEFORE: the *expected* bridgeToken output of the
      //     business call (e.g. Aave.withdraw yields ≈ inputAmount USDT
      //     from the user's aPolUSDT). We pick `floor(inputAmount * 999/1000)`
      //     as a 0.1% margin against rebase rounding so the Executor's
      //     bridgeMinAmount floor doesn't false-positive. This same value
      //     also fills the `amount` slot of the withdraw calldata via the
      //     server-side placeholder substitution.
      if (contractCall !== null) {
        const bridgeMinForBefore =
          position === "BEFORE" && mode === "EXACT_IN"
            ? ((BigInt(baseUnits) * 999n) / 1000n).toString()
            : undefined;
        contractCall.fromAmount =
          mode === "EXACT_OUT"
            ? baseUnits
            : bridgeMinForBefore !== undefined
              ? bridgeMinForBefore
              : "1";
      }

      const isQuoteOnly = contractCall === null;
      const input: Record<string, unknown> = {
        fromChain: fromChain.id,
        fromToken: fromToken.address,
        // Top-level `fromAmount` semantics:
        //   - EXACT_IN: the input the user spends.
        //   - EXACT_OUT with contractCalls: ignored downstream
        //     (LiFiRequestMapper derives the LI.FI top-level toAmount
        //     from the sum of contractCalls[].fromAmount), so we send a
        //     "1" placeholder to satisfy the Zod ">0" guard.
        //   - EXACT_OUT quote-only: the server reinterprets it as the
        //     target toAmount (LI.FI's `QuoteRequestToAmount` shape).
        //     The user pressed "EXACT_OUT" intending the form's amount
        //     to be the destination — pass it verbatim.
        fromAmount:
          mode === "EXACT_IN" ? baseUnits : isQuoteOnly ? baseUnits : "1",
        ...(mode === "EXACT_OUT" ? { mode: "EXACT_OUT" } : {}),
        bridgeToken: effectiveBridgeToken,
        bridgeChain: toChain.id,
        fromAddress: effectiveFromAddress,
        // `integrator` is now part of the API key's IntegratorPolicy
        // (server-side per tenant). The client cannot override it.
        slippage,
        position,
        ...(contractCall !== null ? { contractCalls: [contractCall] } : {}),
      };

      if (position === "BEFORE") {
        // BEFORE wraps everything in Executor.execute. The contract enforces
        // outputMinAmount as the on-chain slippage floor. In EXACT_OUT the
        // floor IS the requested amount; in EXACT_IN we accept any positive
        // delivery for the playground (real integrators set this from their
        // own slippage tolerance against the LI.FI quote).
        input.before = {
          outputToken: toToken.address,
          outputMinAmount: mode === "EXACT_OUT" ? baseUnits : "1",
        };
      }

      await onSubmit({ input, fromToken, toToken });
    },
    [
      validationError,
      humanAmount,
      amountToken,
      fromChain,
      toChain,
      fromToken,
      toToken,
      mode,
      position,
      slippage,
      wallet,
      presetId,
      activePreset,
      customContractAddress,
      customCallData,
      customGasLimit,
      customApprovalAddress,
      onSubmit,
    ],
  );

  // Empty-state: the catalog hasn't loaded yet (no API key, network
  // error, or initial render). Show a guidance message instead of an
  // unusable form. All hooks above are unconditionally called so React's
  // hook-order invariant is preserved across catalog state transitions.
  if (chains.length === 0) {
    return (
      <section className={styles.form}>
        <p className={styles.empty}>
          Pega tu API key para cargar el catálogo de chains y tokens
          disponibles.
        </p>
      </section>
    );
  }

  // Narrow chain/token state once: chains is non-empty, and the useEffect
  // above has run by the time we render here (or will on the next tick).
  // For the one render between catalog-arrival and useEffect-firing we
  // show a brief "Inicializando…" placeholder rather than crashing.
  if (
    fromChain === undefined ||
    toChain === undefined ||
    fromToken === undefined ||
    toToken === undefined ||
    amountToken === undefined
  ) {
    return (
      <section className={styles.form}>
        <p className={styles.empty}>Inicializando…</p>
      </section>
    );
  }

  return (
    <form className={styles.form} onSubmit={onSubmitForm} aria-busy={loading}>
      <div className={styles.row}>
        <ChainTokenPicker
          label="Pagas con"
          chain={fromChain}
          token={fromToken}
          chains={chains}
          wallet={wallet}
          onPickChain={onPickFromChain}
          onPickToken={onPickFromToken}
          forbidTokenAddress={
            fromChain.id === toChain.id ? toToken.address : undefined
          }
          balanceRefreshKey={balanceRefreshKey}
        />

        <button
          type="button"
          className={styles.swap}
          onClick={onSwapDirection}
          title="Intercambiar dirección"
        >
          ⇅
        </button>

        <ChainTokenPicker
          label={position === "BEFORE" ? "Recibes final (output)" : "Recibes"}
          chain={toChain}
          token={toToken}
          chains={chains}
          wallet={wallet}
          onPickChain={onPickToChain}
          onPickToken={onPickToToken}
          forbidTokenAddress={
            fromChain.id === toChain.id ? fromToken.address : undefined
          }
          balanceRefreshKey={balanceRefreshKey}
        />
      </div>

      <div className={styles.row}>
        <fieldset className={styles.modeFieldset}>
          <legend className={styles.legend}>Modo</legend>
          <label className={styles.radio}>
            <input
              type="radio"
              name="mode"
              value="EXACT_IN"
              checked={mode === "EXACT_IN"}
              onChange={() => setMode("EXACT_IN")}
            />
            <span>
              <strong>EXACT_IN</strong> · pago {fromToken.symbol}, calcula
              cuánto recibo
            </span>
          </label>
          <label className={styles.radio}>
            <input
              type="radio"
              name="mode"
              value="EXACT_OUT"
              checked={mode === "EXACT_OUT"}
              disabled={position === "BEFORE"}
              onChange={() => setMode("EXACT_OUT")}
            />
            <span>
              <strong>EXACT_OUT</strong> · quiero {toToken.symbol}, calcula
              cuánto pago
              {position === "BEFORE" && (
                <em className={styles.disabledHint}>
                  {" "}
                  (no soportado en BEFORE)
                </em>
              )}
            </span>
          </label>
        </fieldset>

        <fieldset className={styles.modeFieldset}>
          <legend className={styles.legend}>Position</legend>
          <label className={styles.radio}>
            <input
              type="radio"
              name="position"
              value="AFTER"
              checked={position === "AFTER"}
              onChange={() => onPickPosition("AFTER")}
            />
            <span>
              <strong>AFTER</strong> · swap → contrato (ej. Aave supply)
            </span>
          </label>
          <label className={styles.radio}>
            <input
              type="radio"
              name="position"
              value="BEFORE"
              checked={position === "BEFORE"}
              onChange={() => onPickPosition("BEFORE")}
            />
            <span>
              <strong>BEFORE</strong> · contrato → swap (ej. Aave withdraw)
            </span>
          </label>
        </fieldset>
      </div>

      <div className={styles.row}>
        <label className={styles.amountField}>
          <span className={styles.amountLabel}>
            Cantidad (
            {mode === "EXACT_IN"
              ? `pagas en ${amountToken.symbol}`
              : `recibes en ${amountToken.symbol}`}
            )
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={humanAmount}
            onChange={(e) => setHumanAmount(e.target.value)}
            className={styles.amountInput}
            placeholder="150"
          />
        </label>

        <label className={styles.slippageField}>
          <span className={styles.amountLabel}>Slippage (0–0.05)</span>
          <input
            type="text"
            value={slippage}
            onChange={(e) => setSlippage(e.target.value)}
          />
        </label>
      </div>

      <label className={styles.field}>
        <span className={styles.amountLabel}>
          Contrato target{" "}
          {position === "BEFORE"
            ? "(se ejecuta ANTES del swap)"
            : "(se ejecuta DESPUÉS del swap)"}
        </span>
        <select value={presetId} onChange={(e) => onPickPreset(e.target.value)}>
          <option value={PRESET_NONE}>— Sin contrato (quote only) —</option>
          <option value={PRESET_CUSTOM}>Custom (pega tu propio call)</option>
          {presetsAvailable.length > 0 && (
            <optgroup label={`Presets ${position} en ${toChain.shortName}`}>
              {presetsAvailable.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </optgroup>
          )}
          {presetsAvailable.length === 0 && (
            <option value="_disabled" disabled>
              (sin presets {position} para {toChain.shortName})
            </option>
          )}
        </select>
        {activePreset !== undefined && (
          <small className={styles.presetHint}>
            {activePreset.description}
          </small>
        )}
      </label>

      {presetId === PRESET_CUSTOM && (
        <div className={styles.customGrid}>
          <label className={styles.field} style={{ gridColumn: "1 / -1" }}>
            <span>contractAddress</span>
            <input
              type="text"
              value={customContractAddress}
              onChange={(e) => setCustomContractAddress(e.target.value)}
              placeholder="0x…"
              spellCheck={false}
            />
          </label>
          <label className={styles.field} style={{ gridColumn: "1 / -1" }}>
            <span>callData (hex)</span>
            <textarea
              value={customCallData}
              onChange={(e) => setCustomCallData(e.target.value)}
              placeholder="0x…"
              spellCheck={false}
              rows={3}
              style={{
                fontFamily: "var(--mono)",
                fontSize: "12px",
                resize: "vertical",
              }}
            />
          </label>
          <label className={styles.field}>
            <span>gasLimit</span>
            <input
              type="text"
              value={customGasLimit}
              onChange={(e) => setCustomGasLimit(e.target.value)}
              placeholder="100000"
            />
          </label>
          <label className={styles.field}>
            <span>approvalAddress (opcional)</span>
            <input
              type="text"
              value={customApprovalAddress}
              onChange={(e) => setCustomApprovalAddress(e.target.value)}
              placeholder="0x…"
              spellCheck={false}
            />
          </label>
        </div>
      )}

      {validationError !== undefined && (
        <p className={styles.validationError} role="alert">
          {validationError}
        </p>
      )}

      <button
        type="submit"
        className={styles.submit}
        disabled={loading || validationError !== undefined}
      >
        {loading ? "Calculando…" : "Calcular quote"}
      </button>
    </form>
  );
}

function ChainTokenPicker(props: {
  readonly label: string;
  readonly chain: ChainDef;
  readonly token: TokenDef;
  readonly chains: readonly ChainDef[];
  readonly wallet: WalletState | undefined;
  readonly onPickChain: (id: number) => void;
  readonly onPickToken: (addr: string) => void;
  /**
   * If provided, the token select disables this address (used to forbid
   * picking the same token in both `from` and `to`).
   */
  readonly forbidTokenAddress?: string;
  /** Bump counter forwarded to BalancePill to trigger a refetch. */
  readonly balanceRefreshKey?: number;
}): React.JSX.Element {
  const {
    label,
    chain,
    token,
    chains,
    wallet,
    onPickChain,
    onPickToken,
    forbidTokenAddress,
    balanceRefreshKey,
  } = props;
  return (
    <div className={styles.pickerCol}>
      <span className={styles.pickerLabel}>{label}</span>
      <span className={styles.pickerSubLabel}>Red</span>
      <ChainSelect
        value={chain}
        chains={chains}
        onChange={onPickChain}
        ariaLabel={`${label} chain`}
      />
      <span className={styles.pickerSubLabel}>Token</span>
      <TokenSelect
        value={token}
        tokens={chain.tokens}
        onChange={onPickToken}
        disabledAddress={forbidTokenAddress}
        ariaLabel={`${label} token`}
      />
      <BalancePill
        chainId={chain.id}
        token={token}
        wallet={wallet}
        refreshKey={balanceRefreshKey}
      />
    </div>
  );
}
